import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  TextField,
  IconButton,
  Avatar,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  FileDownloadOutlined,
  FolderOutlined,
  ChangeCircleOutlined,
  PlaceOutlined,
  ErrorOutlineOutlined,
} from '@mui/icons-material';
import { Loader } from '../common/Loader';
import { useDocuments } from '../../hooks/useDocuments';
import { useAuth } from '../../hooks/useAuth';
import { useClinic } from '../../hooks/useClinic';
import { useNavigate } from 'react-router-dom';
import { createManagerContactRequest } from '../../services/requestsService';
import { SuccessPopup } from '../common/SuccessPopup';
import { BackButton } from '../common/BackButton';

type ScreenState =
  | 'account-selection'
  | 'empty-state'
  | 'auth-options'
  | 'credential-method'
  | 'get-credentials-email'
  | 'credentials'
  | 'documents-list';

interface AccountWithAuthStatus {
  id: string;
  name: string;
  isActive: boolean;
  isAuthorized: boolean;
}

export const DocumentsList: React.FC = () => {
  const { isMatched, user, platform } = useAuth();
  const { clinic, branches } = useClinic();
  const navigate = useNavigate();
  const {
    files,
    accounts,
    activeAccountId,
    isLoading,
    handleSwitchAccount,
    handleRequestCredentials,
    handleAuthDocuments,
  } = useDocuments();

  // State machine
  const [screen, setScreen] = useState<ScreenState>('account-selection');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [accountsWithStatus, setAccountsWithStatus] = useState<AccountWithAuthStatus[]>([]);

  // Auth form state
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);

  // Success modal state
  const [successModal, setSuccessModal] = useState<'email' | 'manager' | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);

  // Получаем информацию о клинике и филиале
  const currentBranch = user?.branchId ? branches.find((b) => b.id === user.branchId) : null;
  const clinicName = currentBranch?.name || clinic?.name || 'клиники';
  const workSchedule = currentBranch?.workSchedule || null;

  // Initialize accounts with auth status
  useEffect(() => {
    if (accounts) {
      const acctsWithStatus: AccountWithAuthStatus[] = [
        {
          id: 'parent',
          name: user?.name || accounts.parent.name,
          isActive: activeAccountId === 'parent',
          isAuthorized: accounts.parent.isAuthorized,
        },
        ...accounts.children.map((child) => ({
          id: child.id,
          name: child.name,
          isActive: activeAccountId === child.id,
          isAuthorized: child.isAuthorized,
        })),
      ];
      setAccountsWithStatus(acctsWithStatus);

      // Auto-select first account if not selected
      if (!selectedAccountId && acctsWithStatus.length > 0) {
        setSelectedAccountId('parent');
      }
    }
  }, [accounts, activeAccountId, selectedAccountId, user]);


  if (!isMatched) {
    return (
      <Paper sx={{ p: 3, bgcolor: '#F5F5F5', borderRadius: '24px', textAlign: 'center' }}>
        <Typography sx={{ color: '#999999', mb: 2, lineHeight: 1.2 }}>
          Для доступа к документам необходимо сопоставить карту пациента в{' '}
          <strong onClick={() => navigate('/profile')} style={{ cursor: 'pointer', color: '#007AFF' }}>
            Мой кабинет
          </strong>
        </Typography>
      </Paper>
    );
  }

  if (isLoading) {
    return <Loader message="Загрузка документов..." />;
  }

  const handleAccountSelect = async (accountId: string) => {
    setSelectedAccountId(accountId);
    setAuthError(null);
    setLogin('');
    setPassword('');

    const result = await handleSwitchAccount(accountId);
    if (result.success) {
      // Determine which screen to show based on authorization
      const account = accountsWithStatus.find((a) => a.id === accountId);
      if (!account?.isAuthorized) {
        setScreen('auth-options');
      } else {
        // handleSwitchAccount уже загружает документы, сразу показываем список
        setScreen('documents-list');
      }
    }
  };

  const handleLoginSubmit = async () => {
    setAuthError(null);
    setAuthLoading(true);

    const result = await handleAuthDocuments(login, password, selectedAccountId || undefined);
    setAuthLoading(false);

    if (result.success) {
      setLogin('');
      setPassword('');
      // Сразу показываем список документов (пусть загружаются)
      setScreen('documents-list');
    } else {
      setAuthError((result as any).error || 'Ошибка авторизации');
    }
  };

  const handleGetCredentials = async (type: 'email' | 'manager') => {
    setAuthError(null);
    setRequestLoading(true);

    if (type === 'email') {
      const result = await handleRequestCredentials('email');
      setRequestLoading(false);

      if (result.success) {
        setSuccessModal('email');
      } else {
        setAuthError((result as any).error || 'Ошибка при получении данных');
      }
    } else {
      // Manager contact - отправляем сообщение менеджеру
      try {
        await createManagerContactRequest({ from: (platform || 'MAX').toUpperCase() });
        setRequestLoading(false);
        setSuccessModal('manager');
      } catch (error: any) {
        setRequestLoading(false);
        setAuthError(error.response?.data?.message || 'Ошибка при отправке запроса');
      }
    }
  };

  const renderScreen = () => {
    // ============ SCREEN: Account Selection ============
    if (screen === 'account-selection') {
      return (
        <Box sx={{mb: 0.5, pb: 2 }}>
          {/* Back button */}
          <Box>
            <BackButton onClick={() => navigate(-1)} />
          </Box>

          {/* Header */}
          <Box sx={{ mb: 2, width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '600px' }, margin: '0 auto 16px auto' }}>
            <Typography sx={{ fontSize: { xs: '18px', sm: '20px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1 }}>
              Выберите аккаунт для доступа
            </Typography>
            <Typography sx={{ fontSize: { xs: '18px', sm: '20px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1 }}>
              к документам
            </Typography>
          </Box>

          {/* Accounts list */}
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Loader message="Загрузка аккаунтов..." />
            </Box>
          ) : accountsWithStatus.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <FolderOutlined sx={{ fontSize: { xs: '40px', sm: '48px' }, color: '#CCCCCC', mb: 2 }} />
              <Typography sx={{ fontSize: { xs: '14px', sm: '16px' }, color: '#999999' }}>
                Аккаунты не найдены
              </Typography>
            </Box>
          ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center', width: '100%' }}>
            {accountsWithStatus.map((account) => (
              <Paper
                key={account.id}
                onClick={() => handleAccountSelect(account.id)}
                sx={{
                  width: '100%',
                  maxWidth: { xs: '100%', sm: '480px', md: '600px' },
                  height: { xs: '90px', sm: '100px', md: '110px' },
                  p: { xs: 2, sm: 2.5 },
                  cursor: 'pointer',
                  borderRadius: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: '#ffffff',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  },
                  margin: '0 auto',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                  <Avatar
                    sx={{
                      width: { xs: 56, sm: 64 },
                      height: { xs: 56, sm: 64 },
                      bgcolor: '#007AFF',
                      fontSize: { xs: '18px', sm: '24px' },
                      fontWeight: 600,
                      color: '#ffffff',
                      flexShrink: 0,
                    }}
                  >
                    {account.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: '16px', sm: '20px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1, mb: '8px' }}>
                      {account.name}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, color: 'rgba(33, 33, 33, 0.7)', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1 }}>
                      {account.isAuthorized ? 'Авторизован' : 'Требуется вход'}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, color: '#007AFF', fontWeight: 600, fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1, whiteSpace: 'nowrap', ml: 1, flexShrink: 0 }}>
                  {account.id === 'parent' ? 'Родитель' : 'Ребёнок'}
                </Typography>
              </Paper>
            ))}
            </Box>
          )}
        </Box>
      );
    }

    // ============ SCREEN: Auth Options ============
    if (screen === 'auth-options') {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: 'calc(100vh - 100px)', // Учитываем возможные отступы сверху
          minHeight: '500px', // Минимальная высота для маленьких экранов
        }}>
          {/* Back button */}
          <Box sx={{ flexShrink: 0 }}>
            <BackButton onClick={() => setScreen('account-selection')} />
          </Box>

          {/* Header */}
          <Box sx={{ mb: 2, flexShrink: 0 }}>
            <Typography sx={{ fontSize: { xs: '18px', sm: '20px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1 }}>
              Чтобы войти в личный кабинет,
            </Typography>
            <Typography sx={{ fontSize: { xs: '18px', sm: '20px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1 }}>
              вам понадобится логин и пароль
            </Typography>
          </Box>

          {/* Description */}
          <Typography sx={{ fontSize: { xs: '14px', sm: '16px' }, color: '#999999', mb: 2, fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', fontWeight: 500, lineHeight: 1.2, flexShrink: 0 }}>
            Получить их можно, нажав на кнопку "Получить данные для входа". Если у вас уже есть данные, нажмите кнопку "Войти"
          </Typography>

          {authError && (
            <Paper sx={{ p: 2, bgcolor: '#FFEBEE', borderRadius: '12px', mb: 2, flexShrink: 0 }}>
              <Typography sx={{ fontSize: '14px', color: '#D32F2F', lineHeight: 1.2 }}>
                {authError}
              </Typography>
            </Paper>
          )}

          {/* Flexible spacer that pushes buttons to bottom but keeps them visible */}
          <Box sx={{ flex: 1, minHeight: 20 }} />

          {/* Buttons at bottom */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            alignItems: 'center',
            pb: 2,
            flexShrink: 0,
            width: '100%',
            maxWidth: '359px',
            margin: '0 auto',
          }}>
            <Button
              variant="outlined"
              onClick={() => {
                setAuthError(null);
                setLogin('');
                setPassword('');
                setScreen('credentials');
              }}
              disabled={requestLoading}
              fullWidth
              sx={{
                height: { xs: '48px', sm: '58px' },
                borderColor: '#212121',
                color: '#212121',
                textTransform: 'none',
                borderRadius: '24px',
                fontSize: { xs: '14px', sm: '15px' },
                fontWeight: 600,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                '&:hover': {
                  bgcolor: 'rgba(33, 33, 33, 0.05)',
                },
              }}
            >
              Войти
            </Button>

            <Button
              variant="contained"
              onClick={() => setScreen('credential-method')}
              disabled={requestLoading}
              fullWidth
              sx={{
                height: { xs: '48px', sm: '58px' },
                bgcolor: '#007AFF',
                color: '#FFFFFF',
                textTransform: 'none',
                borderRadius: '24px',
                fontSize: { xs: '14px', sm: '15px' },
                fontWeight: 600,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                '&:hover': {
                  bgcolor: '#0056b3',
                },
              }}
            >
              Получить данные для входа
            </Button>
          </Box>
        </Box>
      );
    }

    // ============ SCREEN: Credential Method Selection ============
    if (screen === 'credential-method') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', minHeight: '500px' }}>
          {/* Back button */}
          <Box sx={{ flexShrink: 0 }}>
            <BackButton onClick={() => setScreen('auth-options')} />
          </Box>

          {/* Header */}
          <Typography sx={{
            fontSize: { xs: '18px', sm: '20px' },
            fontWeight: 600,
            color: '#212121',
            mb: 2,
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            lineHeight: 1,
            width: { xs: '100%', sm: '320px' },
            flexShrink: 0
          }}>
            Чтобы войти в личный кабинет, вам понадобится логин и пароль
          </Typography>

          {/* Description */}
          <Typography sx={{
            fontSize: { xs: '14px', sm: '16px' },
            color: 'rgba(33, 33, 33, 0.7)',
            mb: 3,
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            fontWeight: 500,
            lineHeight: 1.2,
            flexShrink: 0
          }}>
            Получить их можно, нажав на кнопку "Получить данные через Email или менджера". Если у вас уже есть данные, нажмите кнопку "Войти"
          </Typography>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Buttons */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            alignItems: 'center',
            mb: 2,
            flexShrink: 0,
            width: '100%',
            maxWidth: '359px',
            margin: '0 auto',
          }}>
            <Button
              variant="outlined"
              onClick={() => {
                setAuthError(null);
                setLogin('');
                setPassword('');
                setScreen('credentials');
              }}
              disabled={requestLoading}
              fullWidth
              sx={{
                borderColor: '#212121',
                color: '#212121',
                textTransform: 'none',
                borderRadius: '24px',
                height: { xs: '48px', sm: '56px' },
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: 600,
                border: '1px solid #212121',
                '&:hover': {
                  borderColor: '#212121',
                },
              }}
            >
              Войти
            </Button>

            <Button
              variant="outlined"
              onClick={() => setScreen('get-credentials-email')}
              disabled={requestLoading}
              fullWidth
              sx={{
                borderColor: '#007AFF',
                color: '#007AFF',
                textTransform: 'none',
                borderRadius: '24px',
                height: { xs: '48px', sm: '56px' },
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: 600,
                '&:hover': {
                  bgcolor: 'rgba(0, 122, 255, 0.05)',
                },
              }}
            >
              Получить данные через Email
            </Button>

            <Button
              variant="contained"
              onClick={() => handleGetCredentials('manager')}
              disabled={requestLoading}
              fullWidth
              sx={{
                bgcolor: '#007AFF',
                textTransform: 'none',
                borderRadius: '24px',
                height: { xs: '48px', sm: '56px' },
                fontSize: { xs: '14px', sm: '16px' },
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#0055CC',
                },
              }}
            >
              {requestLoading ? 'Отправка...' : 'Получить данные через менджера'}
            </Button>
          </Box>
        </Box>
      );
    }

    // ============ SCREEN: Get Credentials Email ============
    if (screen === 'get-credentials-email') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', minHeight: '500px' }}>
          {/* Back button */}
          <Box sx={{ flexShrink: 0 }}>
            <BackButton onClick={() => setScreen('credential-method')} />
          </Box>

          {/* Header */}
          <Box sx={{ mb: 3, flexShrink: 0 }}>
            <Typography sx={{
              fontSize: { xs: '18px', sm: '20px' },
              fontWeight: 600,
              color: '#212121',
              fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              lineHeight: 1
            }}>
              Получить данные для входа
            </Typography>
            <Typography sx={{
              fontSize: { xs: '18px', sm: '20px' },
              fontWeight: 600,
              color: '#212121',
              fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              lineHeight: 1
            }}>
              в личный кабинет через Email
            </Typography>
          </Box>

          {/* Email input field */}
          <Box sx={{ position: 'relative', mb: 2, flexShrink: 0, width: '100%', maxWidth: '359px', margin: '0 auto' }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={authLoading}
              placeholder="Email"
              style={{
                width: '100%',
                height: '58px',
                borderRadius: '24px',
                backgroundColor: '#F5F5F5',
                border: '1px solid rgba(33, 33, 33, 0.3)',
                paddingLeft: '16px',
                paddingRight: '16px',
                fontSize: '15px',
                fontWeight: 600,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                color: 'rgba(33, 33, 33, 0.7)',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.2s',
                appearance: 'none',
                WebkitAppearance: 'none',
              } as React.CSSProperties}
              onFocus={(e) => {
                (e.target as HTMLInputElement).style.borderColor = '#007AFF';
              }}
              onBlur={(e) => {
                (e.target as HTMLInputElement).style.borderColor = 'rgba(33, 33, 33, 0.3)';
              }}
            />
          </Box>

          {/* Error message */}
          {authError && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              width: '292px',
              minHeight: '51px',
              mb: 2,
              flexShrink: 0,
            }}>
              <ErrorOutlineOutlined sx={{ fontSize: '20px', color: '#D32F2F', flexShrink: 0 }} />
              <Typography sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#D32F2F',
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                lineHeight: 1.3,
              }}>
                {authError}
              </Typography>
            </Box>
          )}

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Send button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2, flexShrink: 0, width: '100%', maxWidth: '359px', margin: '0 auto' }}>
            <Button
              variant="contained"
              onClick={() => handleGetCredentials('email')}
              disabled={!email || requestLoading}
              fullWidth
              sx={{
                height: { xs: '48px', sm: '58px' },
                bgcolor: '#007AFF',
                textTransform: 'none',
                borderRadius: '24px',
                fontSize: { xs: '14px', sm: '15px' },
                fontWeight: 600,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                '&:hover': {
                  bgcolor: '#0055CC',
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(0, 122, 255, 0.3)',
                  color: '#ffffff',
                },
              }}
            >
              {requestLoading ? 'Отправка...' : 'Отправить'}
            </Button>
          </Box>
        </Box>
      );
    }

    // ============ SCREEN: Credentials (Login + Password) ============
    if (screen === 'credentials') {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: 'calc(100vh - 100px)',
          minHeight: '500px',
        }}>
          {/* Back button */}
          <Box sx={{ flexShrink: 0 }}>
            <BackButton onClick={() => setScreen('auth-options')} />
          </Box>

          {/* Title */}
          <Typography sx={{
            fontSize: { xs: '18px', sm: '20px' },
            fontWeight: 600,
            color: '#212121',
            mb: 1.5,
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            flexShrink: 0
          }}>
            Введите логин и пароль
          </Typography>

          {/* Login field */}
          <TextField
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            disabled={authLoading}
            placeholder="Логин"
            sx={{
              width: '100%',
              maxWidth: '359px',
              mb: '5px',
              flexShrink: 0,
              margin: '0 auto',
              '& .MuiOutlinedInput-root': {
                height: '58px',
                borderRadius: '24px',
                bgcolor: '#F5F5F5',
                '& fieldset': {
                  borderColor: 'rgba(33, 33, 33, 0.3)',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(33, 33, 33, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#007AFF',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '15px',
                fontWeight: 600,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                color: '#212121',
                '&::placeholder': {
                  fontSize: '15px',
                  fontWeight: 600,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  color: 'rgba(33, 33, 33, 0.5)',
                  opacity: 1,
                },
              },
            }}
          />

          {/* Password field with eye icon */}
          <Box sx={{ position: 'relative', width: '100%', maxWidth: '359px', height: '58px', mb: 2, flexShrink: 0, margin: '0 auto' }}>
            <input
              type="text"
              value={showPassword ? password : password.replace(/./g, '•')}
              onChange={(e) => {
                const inputValue = e.currentTarget.value;
                // Если пользователь вставил текст (paste), используем его как есть
                // Если печатает - добавляем символы
                if (inputValue.length > password.length) {
                  setPassword(password + inputValue.slice(-1));
                } else if (inputValue.length < password.length) {
                  setPassword(password.slice(0, inputValue.length));
                } else {
                  setPassword(inputValue.replace(/•/g, ''));
                }
              }}
              disabled={authLoading}
              placeholder="Пароль"
              autoComplete="off"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '24px',
                backgroundColor: '#F5F5F5',
                border: '1px solid rgba(33, 33, 33, 0.3)',
                paddingLeft: '16px',
                paddingRight: '44px',
                fontSize: '15px',
                fontWeight: 600,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                color: '#212121',
                boxSizing: 'border-box',
                outline: 'none',
                transition: 'border-color 0.2s',
                appearance: 'none',
                WebkitAppearance: 'none',
              } as React.CSSProperties}
              onFocus={(e) => {
                (e.target as HTMLInputElement).style.borderColor = '#007AFF';
              }}
              onBlur={(e) => {
                (e.target as HTMLInputElement).style.borderColor = 'rgba(33, 33, 33, 0.3)';
              }}
            />
            <IconButton
              onClick={() => setShowPassword(!showPassword)}
              disabled={authLoading}
              sx={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'rgba(33, 33, 33, 0.5)',
                padding: '4px',
                zIndex: 1,
                '&:hover': {
                  bgcolor: 'transparent'
                }
              }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Box>

          {/* Error message */}
          {authError && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1.5,
              width: '305px',
              mb: 2,
              flexShrink: 0,
            }}>
              <ErrorOutlineOutlined sx={{ fontSize: '24px', color: '#D32F2F', flexShrink: 0 }} />
              <Typography sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'rgba(33, 33, 33, 0.7)',
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                lineHeight: 1.3,
                wordBreak: 'break-word',
                flex: 1,
              }}>
                Логин или пароль введён неправильно, перепроверьте данные или обратитесь к менеджеру
              </Typography>
            </Box>
          )}

          {/* Spacer */}
          <Box sx={{ flex: 1, minHeight: 20 }} />

          {/* Login button */}
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pb: 2,
            flexShrink: 0,
            width: '100%',
            maxWidth: '359px',
            margin: '0 auto',
          }}>
            <Button
              variant="contained"
              onClick={handleLoginSubmit}
              disabled={!login || !password || authLoading}
              fullWidth
              sx={{
                height: { xs: '48px', sm: '58px' },
                bgcolor: '#007AFF',
                textTransform: 'none',
                borderRadius: '24px',
                fontSize: { xs: '14px', sm: '15px' },
                fontWeight: 600,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                '&:hover': {
                  bgcolor: '#0055CC',
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(0, 122, 255, 0.3)',
                  color: '#ffffff',
                },
              }}
            >
              {authLoading ? 'Вход...' : 'Войти'}
            </Button>
          </Box>
        </Box>
      );
    }

    // ============ SCREEN: Empty State ============
    if (screen === 'empty-state') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <FolderOutlined sx={{ fontSize: '64px', color: '#CCCCCC', mb: 2 }} />
          <Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#212121', mb: 1 }}>
            Документов нет
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#999999', mb: 3 }}>
            Документы появятся после получения их в клинике
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setScreen('account-selection')}
            sx={{
              borderRadius: '24px',
              textTransform: 'none',
              borderColor: '#007AFF',
              color: '#007AFF',
            }}
          >
            Вернуться к профилям
          </Button>
        </Box>
      );
    }

    // ============ SCREEN: Documents List ============
    if (screen === 'documents-list') {
      return (
        <Box sx={{ pb: 2 }}>
          {/* Back button */}
          <Box sx={{ flexShrink: 0 }}>
            <BackButton onClick={() => setScreen('account-selection')} />
          </Box>

          {/* Header section with title and profile card on same level */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            justifyContent: 'center',
            gap: 2,
            mb: 3,
          }}>
            {/* Title and change account button */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              flex: 1,
              minWidth: 0,
            }}>
              <Typography sx={{ fontSize: { xs: '28px', sm: '36px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1 }}>
                Мои файлы
              </Typography>
              <Button
                onClick={() => setScreen('account-selection')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: '#007AFF',
                  textTransform: 'none',
                  fontSize: { xs: '11px', sm: '13px' },
                  fontWeight: 600,
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  whiteSpace: 'nowrap',
                  width: 'fit-content',
                  pr: 0,
                  mt: 1,
                  '&:hover': {
                    bgcolor: 'rgba(0, 122, 255, 0.05)',
                  },
                }}
              >
                <ChangeCircleOutlined sx={{ fontSize: { xs: '14px', sm: '16px' }, color: '#007AFF' }} />
                Изменить аккаунт
              </Button>
            </Box>

            {/* Profile block */}
            <Paper
              sx={{
                width: { xs: '100%', sm: '359px' },
                maxWidth: '359px',
                height: { xs: '90px', sm: '100px' },
                p: { xs: 2, sm: 2.5 },
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#ffffff',
                flexShrink: 0,
              }}
            >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
              <Avatar
                sx={{
                  width: { xs: 56, sm: 64 },
                  height: { xs: 56, sm: 64 },
                  bgcolor: '#007AFF',
                  fontSize: { xs: '18px', sm: '24px' },
                  fontWeight: 600,
                  color: '#ffffff',
                  flexShrink: 0,
                }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'П'}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: { xs: '16px', sm: '20px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1, mb: '8px' }}>
                  {user?.name || 'Пациент'}
                </Typography>
                <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, color: 'rgba(33, 33, 33, 0.7)', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1 }}>
                  {currentBranch?.name || 'Филиал не выбран'}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#007AFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FolderOutlined sx={{ fontSize: '20px', color: '#ffffff' }} />
            </Box>
          </Paper>
          </Box>

          {/* Files list */}
          {files.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4 }}>
              <Box
                component="img"
                src="/icons/clinical-notes.svg"
                sx={{ width: '40px', height: '40px', mb: 2 }}
              />
              <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1 }}>
                На данный момент
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1, mb: 2 }}>
                файлы отсутствуют
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'rgba(33, 33, 33, 0.7)', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1 }}>
                Здесь будут появляться документы
              </Typography>
            </Box>
          ) : (
            <List sx={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
              {files.map((file, idx) => (
                <Paper
                  key={file.title || idx}
                  sx={{
                    width: { xs: '100%', sm: '359px' },
                    maxWidth: '359px',
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: '16px',
                    bgcolor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {/* File title */}
                  <Typography sx={{ fontSize: { xs: '16px', sm: '20px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', lineHeight: 1.2 }}>
                    {file.title || `Документ ${idx + 1}`}
                  </Typography>

                  {/* File details */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        component="img"
                        src="/icons/calendar-check.svg"
                        sx={{ width: { xs: '18px', sm: '20px' }, height: { xs: '18px', sm: '20px' } }}
                      />
                      <Typography sx={{ fontSize: { xs: '13px', sm: '14px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
                        {file.date}
                      </Typography>
                    </Box>

                    {/* Doctor/Specialist */}
                    {file.doctor && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          component="img"
                          src="/icons/doctor-specialist.svg"
                          sx={{ width: { xs: '18px', sm: '20px' }, height: { xs: '18px', sm: '20px' } }}
                        />
                        <Typography sx={{ fontSize: { xs: '13px', sm: '14px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
                          {file.doctor}
                        </Typography>
                      </Box>
                    )}

                    {/* Branch */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PlaceOutlined sx={{ fontSize: { xs: '18px', sm: '20px' }, color: '#212121' }} />
                      <Typography sx={{ fontSize: { xs: '13px', sm: '14px' }, fontWeight: 600, color: '#212121', fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
                        {currentBranch?.name || 'Филиал'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Download button */}
                  {file.urls && file.urls.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', mt: 1, alignItems: 'center', width: '100%', maxWidth: '100%' }}>
                      {file.urls.map((url: string, urlIdx: number) => (
                        <Button
                          key={urlIdx}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="contained"
                          fullWidth
                          sx={{
                            maxWidth: '311px',
                            height: { xs: '48px', sm: '58px' },
                            bgcolor: '#007AFF',
                            color: '#ffffff',
                            textTransform: 'none',
                            borderRadius: '12px',
                            fontSize: { xs: '13px', sm: '15px' },
                            fontWeight: 600,
                            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            '&:hover': {
                              bgcolor: '#0055CC',
                            },
                          }}
                        >
                          <FileDownloadOutlined sx={{ fontSize: { xs: '16px', sm: '20px' }, width: { xs: '16px', sm: '20px' }, height: { xs: '16px', sm: '20px' } }} />
                          Скачать {file.urls.length > 1 && `(${urlIdx + 1})`}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
            </List>
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <Box>
      {/* Main Content */}
      {renderScreen()}

      {/* Success Email Modal */}
      <SuccessPopup
        open={successModal === 'email'}
        onClose={() => {
          setSuccessModal(null);
          setScreen('auth-options');
          setEmail('');
        }}
        clinicName={clinicName}
        workSchedule={workSchedule}
        type="email"
      />

      {/* Success Manager Contact Modal */}
      <SuccessPopup
        open={successModal === 'manager'}
        onClose={() => {
          setSuccessModal(null);
          setScreen('auth-options');
        }}
        clinicName={clinicName}
        workSchedule={workSchedule}
        type="manager"
      />
    </Box>
  );
};
